import { useRef } from "react";

import {
  faArrowDownToLine,
  faArrowUpFromLine,
} from "@fortawesome/pro-regular-svg-icons";
import saveAs from "file-saver";
import { observer } from "mobx-react-lite";
import Dropzone, { DropzoneRef } from "react-dropzone";
import { v4 as uuidV4 } from "uuid";

import { informPreparingDownload } from "src/api/downloadUtils";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { BeMappedNode, MatrixTableNodeData } from "src/constants/NodeDataTypes";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import {
  useExportMatrixTable,
  useImportMatrixTable,
} from "src/matrixTableNode/queries";
import { isMatrixNodeInDefaultState } from "src/matrixTableNode/utils";
import { useGraphStore } from "src/store/StoreProvider";
import { errorMessage } from "src/utils/stringUtils";

const downloadTemplate = () => {
  const templateData =
    "RESULT,data.result,data.condition_b\nDEFAULT,None,is any\ndata.condition_a,is any,result";
  const blob = new Blob([templateData], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "2d_matrix_template.csv";
  link.click();
  URL.revokeObjectURL(url);
  toastActions.success({
    title: "Downloaded template successfully",
  });
};

export const MatrixNodeLogicTitle: React.FC<{
  flowVersionId: string;
  selectedNode: BeMappedNode;
}> = observer(({ flowVersionId, selectedNode }) => {
  const { mutateAsync: importMatrixTable, isPending: isUploading } =
    useImportMatrixTable();
  const { mutateAsync: exportMatrixTable, isPending: isExporting } =
    useExportMatrixTable();
  const dropzoneRef = useRef<DropzoneRef>(null);
  const { updateNode } = useGraphStore();
  const { isOpen, openModal, closeModal } = useModal();

  const openDropzone = () => {
    dropzoneRef.current?.open();
  };

  const downloadMatrixCsv = async (flowVersionId: string, nodeId: string) => {
    const toastId = uuidV4();
    informPreparingDownload(toastId);
    try {
      const response = await exportMatrixTable({
        flowVersionId,
        nodeId,
      });

      saveAs(response, `${selectedNode.data.label}.csv`);
      toastActions.success({
        title: "CSV downloaded successfully",
        id: toastId,
      });
    } catch (error) {
      toastActions.failure({
        title: "Failed to download CSV",
        description: errorMessage(error),
        id: toastId,
      });
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0 && selectedNode?.id) {
      const file = acceptedFiles[0];
      const toastId = toastActions.loading({
        title: `Importing ${file.name}`,
      });
      try {
        const response = await importMatrixTable({
          flowVersionId,
          nodeId: selectedNode.id,
          file,
        });
        const matrixNodeMeta = response.data;

        updateNode({
          nodeId: selectedNode.id,
          newData: {
            row_field: matrixNodeMeta.row_field,
            column_field: matrixNodeMeta.column_field,
            result_field: matrixNodeMeta.result_field,
            column_predicates: matrixNodeMeta.column_predicates,
            row_predicates: matrixNodeMeta.row_predicates,
            grid_effects: matrixNodeMeta.grid_effects,
            fallback: matrixNodeMeta.fallback,
          },
        });

        toastActions.success({
          title: "Imported successfully",
          id: toastId,
        });
      } catch (error) {
        toastActions.failure({
          title: "Error importing",
          description: errorMessage(error),
          id: toastId,
        });
      }
    }
  };

  const optionElements = [
    {
      key: "Import table",
      icon: faArrowUpFromLine,
      action: isMatrixNodeInDefaultState(
        selectedNode.data as MatrixTableNodeData,
      )
        ? openDropzone
        : openModal,
      disabled: isUploading || isExporting,
    },
    {
      key: "Download CSV template",
      icon: faArrowDownToLine,
      action: downloadTemplate,
      disabled: isUploading || isExporting,
    },
    {
      key: "Export table as CSV",
      icon: faArrowDownToLine,
      action: () => {
        downloadMatrixCsv(flowVersionId, selectedNode.id);
      },
      disabled: isUploading || isExporting,
    },
  ];

  return (
    <div className="flex items-center gap-2">
      Logic
      <EllipsisOptionsDropdown
        dataLoc="table-options-dropdown"
        elements={optionElements}
      />
      <div className="hidden">
        <Dropzone
          ref={dropzoneRef}
          accept={{
            "text/csv": [".csv"],
          }}
          maxFiles={1}
          noClick={true}
          noKeyboard={true}
          onDrop={onDrop}
          onFileDialogOpen={closeModal}
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>
      </div>
      <ConfirmationModal
        description="Importing another file would overwrite the existing table. Do you
          still wish to Import?"
        open={isOpen}
        title="Overwrite existing table"
        onClose={closeModal}
        onConfirm={openDropzone}
      />
    </div>
  );
});
